import { createAsyncThunk } from '@reduxjs/toolkit';
import { showToast } from '@/store/notifications/slice';
import chevron from '@/services/chevron';
import { runCraftJsonMigrations } from '@builder4/shared/migrations/craft';
import { addSentryBreadcrumb, captureSentryException } from '@/utils/sentry';
import type {
  CreatePageResponse,
  PageListItem,
  PageResponse,
  UpdatePageRequest,
  PublishPageResponse,
  PublishedLandingPageCountResponse,
} from './types';
import { convertToSlug } from '@/utils/slugHelper';
import {
  isConflictError,
  isNetworkError,
  getPageFetchErrorType,
  getNetworkErrorToast,
  getSaveSuccessToast,
  getSaveFailureToast,
  isBadRequestError,
} from '@/utils/helperFunc';
import { resetPublishing, setSelectedPath } from './slice';
import type { RootState } from '../store';
import { selectSelectedDomain } from '../domains/selectors';
import { selectCurrentPage, selectConsecutiveSaveFailures, selectSelectedPath } from './selectors';
import { CURRENT_SCHEMA_VERSION } from '@builder4/shared/migrations/craft/registry';
import { CraftJsonDto } from '@builder4/shared/dto/craft';
import type { VersionedSerializedNodes } from '@builder4/shared/dto/craft/CraftJsonDto';
import { validate } from 'class-validator';
import { formatValidationErrors } from '@builder4/shared/utils/validators';
import { domainAndPathValidation, pageNameValidation } from './utils';
import { setNavbarTitleDropdownOpen } from '../system/systemSlice';
import { CONTENT_BLOCK_NAME } from '@/utils/constants';
import { defaultProps as defaultContainerProps } from '@builder4/shared/components/Container';

const GENERIC_PUBLISH_ERROR_MESSAGE =
  'Error publishing page. Please contact support if this happens again.';

const GENERIC_UNPUBLISH_ERROR_MESSAGE =
  'Error unpublishing page. Please contact support if this happens again.';

/**
 * Safely parse a Chevron error response message
 *
 * Handles both XMLHttpRequest and Error instances.
 * Chevron almost always returns an XMLHttpRequest instance, but
 * can return an Error instance in some cases.
 *
 * @param error
 * @returns The error message if available, undefined otherwise
 */
const parseChevronXmlErrorMessage = (error: XMLHttpRequest): string | undefined => {
  try {
    const parsed = JSON.parse(error.responseText);
    return parsed.message || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Handle publishing errors with appropriate user feedback and error reporting
 * Only XMLHttpRequest errors can show specific error messages to users.
 * Generic JS errors use a generic message and are reported to Sentry.
 */
const handlePublishingError = (
  error: unknown,
  dispatch: (action: ReturnType<typeof showToast>) => void,
  context: { pageId: string; domain: string; path: string },
) => {
  if (error instanceof XMLHttpRequest) {
    // Handle XMLHttpRequest errors with specific messages
    const errorMsg = parseChevronXmlErrorMessage(error);

    if (isNetworkError(error)) {
      dispatch(
        showToast({
          text: 'Network error while publishing. Please check your connection and try again.',
          state: 'warning',
        }),
      );
      return;
    }

    if (isConflictError(error) || isBadRequestError(error)) {
      const message = errorMsg || 'An error has occurred during publishing.';
      dispatch(
        showToast({
          text: `${message}. Please check your page settings and try again.`,
          state: 'error',
        }),
      );
      return;
    }

    // Other XMLHttpRequest errors
    const message = errorMsg || GENERIC_PUBLISH_ERROR_MESSAGE;
    dispatch(
      showToast({
        text: message,
        state: 'error',
      }),
    );
    return;
  }

  // Generic JS errors: don't expose actual error messages
  const errorToReport =
    error instanceof Error ? error : new Error('Unexpected error during page publishing');

  captureSentryException(errorToReport, {
    tags: { feature: 'page-publishing', severity: 'critical' },
    extra: { ...context, errorType: 'unexpected' },
  });

  dispatch(
    showToast({
      text: GENERIC_PUBLISH_ERROR_MESSAGE,
      state: 'error',
    }),
  );
};

// GET → fetch page by id
export const fetchPageById = createAsyncThunk<PageResponse, string>(
  'page/fetchPageById',
  async (pageId: string, { dispatch, rejectWithValue }) => {
    addSentryBreadcrumb('Fetching page by ID', { pageId });
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages/${pageId}`;
    try {
      const response = (await chevron.http({
        url,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })) as PageResponse;

      const dto = CraftJsonDto.fromPage(response.craftJson);
      const migrationResult = runCraftJsonMigrations(dto);
      if (migrationResult.error) {
        const error = new Error(`Migration error: ${migrationResult.errorMessage}`);
        captureSentryException(error, {
          tags: { feature: 'page-loading', operation: 'migration' },
          extra: { pageId, errorMessage: migrationResult.errorMessage },
        });
        console.error('Migration error: ', migrationResult.errorMessage);
        throw error;
      }
      const migratedCraftJson = migrationResult.json;
      const validationResult = await validate(migratedCraftJson);

      if (validationResult.length > 0) {
        const validationErrors = formatValidationErrors(validationResult);
        const error = new Error('Invalid page craftJson');
        captureSentryException(error, {
          tags: { feature: 'page-loading', operation: 'validation' },
          extra: { pageId, validationErrors },
        });
        console.error('Invalid page craftJson: ', validationErrors);
      }

      addSentryBreadcrumb('Page loaded successfully', { pageId });
      return {
        ...response,
        craftJson: migratedCraftJson.toPageFormat(),
      };
    } catch (error) {
      const errorType = getPageFetchErrorType(error);
      if (errorType === 'NETWORK_ERROR') {
        dispatch(showToast(getNetworkErrorToast()));
        return rejectWithValue('NETWORK_ERROR');
      }
      if (errorType === 'PAGE_NOT_FOUND') {
        return rejectWithValue('PAGE_NOT_FOUND');
      }
      // Handle all other errors (400, 500, migration errors, validation errors, etc.)
      // as PAGE_INVALID to show disabled Builder with error message
      return rejectWithValue('PAGE_INVALID');
    }
  },
);

// POST → create new page
export const createPage = createAsyncThunk<CreatePageResponse>(
  'page/createPage',
  async (_, { dispatch }) => {
    addSentryBreadcrumb('Creating new page');
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages`;
    try {
      // Generate a unique node ID for the default container
      const containerNodeId = `container_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create default container node
      const defaultContainer = {
        type: {
          resolvedName: CONTENT_BLOCK_NAME,
        },
        isCanvas: true,
        props: {
          ...defaultContainerProps,
          flexDirection: 'row',
          disableMaxWidth: true,
        },
        displayName: CONTENT_BLOCK_NAME,
        custom: {
          displayName: CONTENT_BLOCK_NAME,
          isLocked: false,
        },
        hidden: false,
        nodes: [],
        linkedNodes: {},
        parent: 'ROOT',
      };

      const response = (await chevron.http({
        url,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          craft_json: {
            version: CURRENT_SCHEMA_VERSION,
            ROOT: {
              type: {
                resolvedName: 'Page',
              },
              isCanvas: true,
              props: {
                fillSpace: 'yes',
                padding: [0, 0, 0, 0],
                backgroundColor: '$ref:#/colors/color6',
                radius: 0,
                maxWidth: '100%',
                height: 'auto',
                gap: 0,
                bgImageScale: 100,
                bgImageOpacity: 100,
                bgImageMode: 'fill',
                bgImagePosition: 'center center',
                breakpoint: 'desktop',
              },
              displayName: 'Page',
              custom: {
                displayName: 'Landing Page',
                isLocked: false,
              },
              hidden: false,
              nodes: [containerNodeId],
              linkedNodes: {},
            },
            [containerNodeId]: defaultContainer,
          },
        },
      })) as CreatePageResponse;

      addSentryBreadcrumb('Page created successfully', { pageId: response.id });
      return response;
    } catch (error) {
      if (isNetworkError(error)) {
        dispatch(showToast(getNetworkErrorToast()));
      }
      throw error;
    }
  },
);

// GET → fetch pages list
export const fetchPagesList = createAsyncThunk<PageListItem[]>(
  'pages/fetchPagesList',
  async (_, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages`;
    try {
      const response = (await chevron.http({
        url,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })) as PageListItem[];

      return response;
    } catch (error) {
      if (isNetworkError(error)) {
        dispatch(showToast(getNetworkErrorToast()));
      }
      throw error;
    }
  },
);

const checkForEmptyRootNodes = (craftJson: VersionedSerializedNodes) => {
  if (craftJson && craftJson.ROOT.nodes.length === 0) {
    return true;
  }
  return false;
};

// PATCH → update page by id
export const updatePageById = createAsyncThunk<
  PageResponse,
  { pageId: string; body: UpdatePageRequest }
>('page/updatePageById', async ({ pageId, body }, { dispatch, getState }) => {
  const state = getState() as RootState;
  const currentFailures = selectConsecutiveSaveFailures(state);
  addSentryBreadcrumb('Updating page', { pageId, hasName: !!body.name });
  const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages/${pageId}`;

  // The CraftJS schema version is not managed by CraftJS. We set expected version here.
  if (body.craft_json) {
    body.craft_json.version = CURRENT_SCHEMA_VERSION;
    if (checkForEmptyRootNodes(body.craft_json)) {
      // Guard against mass node deletion bug by preventing saving a page with no nodes
      dispatch(showToast(getSaveFailureToast(currentFailures + 1)));
      const nodeCheckError = new Error('Invalid page craftJson: ROOT node has no nodes');
      captureSentryException(nodeCheckError, {
        tags: { feature: 'page-update' },
      });
      throw nodeCheckError;
    }
  }

  try {
    const response = (await chevron.http({
      url,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body,
    })) as PageResponse;

    // Show success toast only when recovering from previous failures
    if (currentFailures > 0) {
      dispatch(showToast(getSaveSuccessToast()));
    }
    addSentryBreadcrumb('Page updated successfully', { pageId });
    return response;
  } catch (error) {
    if (isNetworkError(error)) {
      dispatch(showToast(getNetworkErrorToast()));
    } else {
      const failuresAfterThis = currentFailures + 1;
      dispatch(showToast(getSaveFailureToast(failuresAfterThis)));
    }
    throw error;
  }
});

export const publishPage = createAsyncThunk<
  PublishPageResponse,
  { pageId: string; tags?: string[] }
>('page/publishPage', async ({ pageId, tags }, { dispatch, getState, rejectWithValue }) => {
  addSentryBreadcrumb('Publishing page', { pageId, tags });
  const state = getState() as RootState;
  const currentPage = selectCurrentPage(state);
  const domain = selectSelectedDomain(state) || currentPage?.domain || '';
  let path = selectSelectedPath(state) || '';
  const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages/${pageId}/publish`;
  const name = currentPage?.name ?? null;

  try {
    pageNameValidation(name);
  } catch (e) {
    const errorMessage = (e as Error).message;
    dispatch(
      showToast({
        text: `${errorMessage}`,
        state: 'error',
      }),
    );
    // Open the navbar title dropdown to let user add/edit page name
    dispatch(setNavbarTitleDropdownOpen(true));
    return rejectWithValue('No Page Name');
  }

  if (!path && name) {
    path = convertToSlug(name);
  }

  try {
    domainAndPathValidation(domain, path);
  } catch (e) {
    const errorMessage = (e as Error).message;
    dispatch(
      showToast({
        text: `${errorMessage}. Please check your page settings and try again.`,
        state: 'error',
      }),
    );
    return rejectWithValue('domain_or_path_validation_error');
  }

  try {
    const response = (await chevron.http({
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: {
        domain,
        path,
        tags: tags || [],
      },
    })) as PublishPageResponse;

    // Schedule the reset
    setTimeout(() => {
      dispatch(resetPublishing());
    }, 1500);

    // Sync the selectedPath (slug) property with the publishedPage
    dispatch(setSelectedPath(response.page.path));

    addSentryBreadcrumb('Page published successfully', {
      pageId,
      domain,
      path: response.page.path,
    });
    return response;
  } catch (error: unknown) {
    handlePublishingError(error, dispatch, { pageId, domain, path });
    throw error;
  }
});

export const fetchPublishedLandingPageCount = createAsyncThunk<PublishedLandingPageCountResponse>(
  'pages/fetchPublishedLandingPageCount',
  async () => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages/published-landing-page-count`;
    const response = (await chevron.http({
      url,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })) as PublishedLandingPageCountResponse;

    return response;
  },
);

export const unpublishPage = createAsyncThunk<PageResponse, { pageId: string }>(
  'page/unpublishPage',
  async ({ pageId }, { dispatch }) => {
    addSentryBreadcrumb('Unpublishing page', { pageId });
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/pages/${pageId}/unpublish`;

    try {
      const response = (await chevron.http({
        url,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })) as PageResponse;

      addSentryBreadcrumb('Page unpublished successfully', { pageId });
      return response;
    } catch (error: unknown) {
      if (error instanceof XMLHttpRequest) {
        const errorMsg = parseChevronXmlErrorMessage(error);

        if (isNetworkError(error)) {
          dispatch(
            showToast({
              text: 'Network error while unpublishing. Please check your connection and try again.',
              state: 'warning',
            }),
          );
          throw error;
        }

        const message = errorMsg || GENERIC_UNPUBLISH_ERROR_MESSAGE;
        dispatch(showToast({ text: message, state: 'error' }));
        throw error;
      }

      const errorToReport =
        error instanceof Error ? error : new Error('Unexpected error during page unpublishing');

      captureSentryException(errorToReport, {
        tags: { feature: 'page-unpublishing', severity: 'critical' },
        extra: { pageId, errorType: 'unexpected' },
      });

      dispatch(showToast({ text: GENERIC_UNPUBLISH_ERROR_MESSAGE, state: 'error' }));
      throw error;
    }
  },
);
